import React, { useEffect, useRef, useState } from 'react';
import { t } from '../../i18n/strings.js';
import { usePlans } from '../../lib/pricing.js';
import { track } from '../../lib/track.js';
import OfferHeader from './offer/OfferHeader.jsx';
import { useOfferTimer } from './offer/OfferTimer.jsx';
import PersonalizedPlanPreview from './offer/PersonalizedPlanPreview.jsx';
import BenefitsSection from './offer/BenefitsSection.jsx';
import PlanSelector from './offer/PlanSelector.jsx';
import SocialProofCarousel from './offer/SocialProofCarousel.jsx';
import FAQAccordion from './offer/FAQAccordion.jsx';
import SecureCheckout from './offer/SecureCheckout.jsx';
import GuaranteeSection from './offer/GuaranteeSection.jsx';
import CTA from './offer/CTA.jsx';
import { GRADIENT_BTN } from './offer/shared.js';

// ============================================================================
// Página de oferta (resultado + planos + checkout) — rolagem única, todos os
// mercados. Refatorada em 05/10/2026 em componentes (pasta ./offer/).
//
// ORDEM: barra de oferta (timer + "Ver meu plano") → título → prévia real do
// plano → benefícios → 1º SELETOR → prova social → FAQ → 2º SELETOR →
// checkout seguro → garantia → CTA final.
//
// ESTADO ÚNICO: este componente é dono de `plans` (Stripe via usePlans),
// `selectedId` e do timer. Os dois PlanSelector recebem as MESMAS props, então
// escolher um plano em qualquer um reflete no outro. O botão de checkout dos
// dois chama o mesmo `checkout()` → onContinue(selectedPlan) → startCheckout
// (App.jsx) → CheckoutScreen → create-checkout-session (Stripe).
//
// BR x internacional: decidido em lib/pricing.js (currentLanguage==='pt' →
// BRL → só o plano semanal; demais idiomas → mensal+anual).
// ============================================================================

export default function PaywallIntl({ step, gender, answers, onContinue }) {
  const timerLabel = useOfferTimer(600);
  const { status, plans, retry } = usePlans();

  const [selectedId, setSelectedId] = useState(null);
  const effectiveId = plans.find((p) => p.id === selectedId)?.id ?? plans.find((p) => p.highlight)?.id ?? plans[0]?.id;
  const selectedPlan = plans.find((p) => p.id === effectiveId);

  const firstRef = useRef(null);
  const secondRef = useRef(null);
  const heroEndRef = useRef(null);
  const [pastHero, setPastHero] = useState(false);
  const [selectorVisible, setSelectorVisible] = useState({ a: false, b: false });

  // tracking: visualização da oferta (uma vez)
  useEffect(() => {
    track('paywall_view', { language: document.documentElement.lang || undefined });
  }, []);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;
    const heroObs = new IntersectionObserver(([e]) => setPastHero(!e.isIntersecting && e.boundingClientRect.top < 0));
    const mk = (k) => new IntersectionObserver(([e]) => setSelectorVisible((s) => ({ ...s, [k]: e.isIntersecting })));
    const a = mk('a');
    const b = mk('b');
    if (heroEndRef.current) heroObs.observe(heroEndRef.current);
    if (firstRef.current) a.observe(firstRef.current);
    if (secondRef.current) b.observe(secondRef.current);
    return () => {
      heroObs.disconnect();
      a.disconnect();
      b.disconnect();
    };
  }, []);
  const showSticky = pastHero && !selectorVisible.a && !selectorVisible.b;

  function scrollToPlans() {
    firstRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
  function select(id) {
    setSelectedId(id);
    const p = plans.find((x) => x.id === id);
    if (p) track('select_item', { item_id: p.planId, currency: p.currency, value: (p.promoAmount ?? p.amount) / 100 });
  }
  function checkout(plan = selectedPlan) {
    if (!plan) return;
    track('begin_checkout', { item_id: plan.planId, currency: plan.currency, value: (plan.promoAmount ?? plan.amount) / 100 });
    onContinue(plan);
  }

  const selectorProps = {
    plans,
    status,
    selectedId: effectiveId,
    onSelect: select,
    timerLabel,
    onCheckout: checkout,
    onRetry: retry,
  };

  return (
    <div className="phone-frame flex flex-col safe-top safe-bottom">
      <OfferHeader timerLabel={timerLabel} onGetPlan={scrollToPlans} />

      <div className="px-5 pb-28 flex flex-col gap-8">
        <h1
          className="font-display font-extrabold text-center mt-4"
          style={{ fontSize: 'clamp(34px, 10.4vw, 42px)', lineHeight: 1.08 }}
        >
          {t('paywallIntl.planReadyLead')} <span className="text-cyan">{t('paywallIntl.planReadyHighlight')}</span>{' '}
          {t('paywallIntl.planReadyTail')}
        </h1>

        <div className="-mt-4">
          <PersonalizedPlanPreview answers={answers} gender={gender} onUnlock={scrollToPlans} />
          <div ref={heroEndRef} className="h-px" />
        </div>

        <BenefitsSection />

        <PlanSelector
          ref={firstRef}
          title={t('paywallIntl.convertTitle')}
          subtitle={t('paywallIntl.convertSub')}
          {...selectorProps}
        />

        <SocialProofCarousel images={step.intlTestimonialImages} />

        <FAQAccordion />

        <PlanSelector ref={secondRef} title={t('paywallIntl.chooseTitle')} {...selectorProps} />

        <SecureCheckout methods={step.paymentMethods} />

        <GuaranteeSection />

        <div className="text-center">
          <h2 className="font-display font-extrabold text-[26px] leading-tight">{t('paywallIntl.finalTitle')}</h2>
          <p className="text-[15px] text-ink-muted mt-2 mb-4">{t('paywallIntl.finalSub')}</p>
          <CTA onClick={() => checkout()} disabled={!selectedPlan}>
            {t('paywallIntl.unlockCta')}
          </CTA>
        </div>
      </div>

      {/* CTA fixo: aparece depois do topo, some quando um seletor está na tela */}
      <div
        className={`fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[430px] z-30 px-4 pt-3 transition-all duration-300 ${
          showSticky ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0 pointer-events-none'
        }`}
        style={{
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)',
          background: 'linear-gradient(to top, #0B0F14 70%, rgba(11,15,20,0))',
        }}
        aria-hidden={!showSticky}
      >
        <div className="flex items-center justify-between gap-3 rounded-2xl bg-surface-2 border border-white/10 pl-4 pr-2 py-2">
          <span className="font-display font-bold text-[14px] leading-tight">{t('paywallIntl.stickyText')}</span>
          <button
            onClick={scrollToPlans}
            tabIndex={showSticky ? 0 : -1}
            className="h-11 px-5 rounded-xl font-display font-extrabold text-[14px] text-bg"
            style={{ background: GRADIENT_BTN }}
          >
            {t('paywallIntl.stickyCta')}
          </button>
        </div>
      </div>
    </div>
  );
}
